import React, { useEffect, useState } from 'react';
import { Modal, Table, Form, Badge, Button, Placeholder } from 'react-bootstrap';
import { IconAlertTriangle, IconSearch, IconTool, IconBan } from '@tabler/icons-react';
import useDashboardIncompleteBookings, { IncompleteBookingType } from '../../../services/dashboard-v2/hooks/useDashboardIncompleteBookings';
import { IncompleteBookingItem } from '../../../services/dashboard-v2/schemas/dashboardV2Schemas';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';

const PER_PAGE = 10;

interface NeedsAttentionModalProps {
    show: boolean;
    propertyId: string;
    onClose: () => void;
    onFixIt: (bookingId: string) => void;
}

const MissingBadges: React.FC<{ item: IncompleteBookingItem }> = ({ item }) => (
    <div className="d-flex gap-1 flex-wrap">
        {!item.room_type_id && <Badge bg="" style={{ backgroundColor: '#FBE6E6', color: '#D63939', fontSize: '9px', fontWeight: 700 }}>NO ROOM TYPE</Badge>}
        {!item.rate_plan_id && <Badge bg="" style={{ backgroundColor: '#FBF1DE', color: '#C07A1E', fontSize: '9px', fontWeight: 700 }}>NO RATE PLAN</Badge>}
        {!item.room_id && <Badge bg="" style={{ backgroundColor: '#E6EDF9', color: '#3468B0', fontSize: '9px', fontWeight: 700 }}>NO ROOM</Badge>}
    </div>
);

const NeedsAttentionModal: React.FC<NeedsAttentionModalProps> = ({ show, propertyId, onClose, onFixIt }) => {
    const [search, setSearch] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');
    const [type, setType] = useState<IncompleteBookingType>('all');
    const [page, setPage] = useState(1);

    // Debounce search supaya tidak nembak request tiap ketikan.
    useEffect(() => {
        const t = setTimeout(() => setSearchDebounced(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    // Jaring pengaman: kalau ternyata masih ada di halaman > 1 saat hasil
    // search/filter berganti, balik ke halaman 1. Reset UTAMA-nya dilakukan
    // langsung di handler (lihat handleSearchChange/handleTypeChange) supaya
    // tidak sempat ada satu request nyasar dengan kombinasi filter-baru +
    // halaman-lama.
    useEffect(() => {
        setPage(1);
    }, [searchDebounced, type]);

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setPage(1);
    };

    const handleTypeChange = (value: IncompleteBookingType) => {
        setType(value);
        setPage(1);
    };

    useEffect(() => {
        if (!show) {
            setSearch('');
            setSearchDebounced('');
            setType('all');
            setPage(1);
        }
    }, [show]);

    const { data, isLoading } = useDashboardIncompleteBookings({
        // Modal ini selalu ter-mount di DashboardDesignPage (react-bootstrap
        // Modal mengurus visibilitasnya sendiri), jadi tanpa guard ini setiap
        // load dashboard menembak 1 request tambahan untuk data yang tidak
        // pernah dilihat. property_id kosong => hook-nya `enabled: false`.
        property_id: show ? propertyId : undefined,
        type,
        search: searchDebounced,
        page,
        per_page: PER_PAGE,
    });

    const items = data?.data.list.items ?? [];
    const meta = data?.data.list.meta;

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="fs-5 d-flex align-items-center gap-2">
                    <IconAlertTriangle size={18} className="text-warning" />
                    Needs Attention{meta ? ` — ${meta.total} reservations` : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0">
                <div className="d-flex gap-2 flex-wrap p-3 border-bottom">
                    <div className="d-flex align-items-center border rounded-2 px-2 flex-grow-1" style={{ minWidth: 200 }}>
                        <IconSearch size={14} className="text-muted flex-shrink-0" />
                        <Form.Control
                            type="text"
                            placeholder="Search guest name or invoice…"
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="border-0 shadow-none"
                            style={{ fontSize: '13px' }}
                        />
                    </div>
                    <Form.Select
                        value={type}
                        onChange={(e) => handleTypeChange(e.target.value as IncompleteBookingType)}
                        style={{ maxWidth: 220, fontSize: '12.5px' }}
                    >
                        <option value="all">All categories</option>
                        <option value="missing_room_type">Missing Room Type</option>
                        <option value="missing_rate_plan">Missing Rate Plan</option>
                        <option value="missing_room">Missing Room</option>
                    </Form.Select>
                </div>

                <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                    {isLoading ? (
                        <Placeholder as="div" animation="glow" className="p-4">
                            <Placeholder xs={12} size="lg" className="mb-3" />
                            <Placeholder xs={12} className="mb-2" />
                            <Placeholder xs={12} className="mb-2" />
                        </Placeholder>
                    ) : items.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="mb-3 text-muted">
                                <IconBan size={40} stroke={1} color="#E0E0E0" />
                            </div>
                            <p className="text-muted mb-0" style={{ fontSize: '12.5px' }}>No matching reservations.</p>
                        </div>
                    ) : (
                        <Table borderless responsive style={{ fontSize: '12px', margin: 0 }}>
                            <tbody>
                                {items.map((item) => (
                                    <tr key={item.booking_id} style={{ borderBottom: '1px solid #F8F9FA' }}>
                                        <td className="py-2 px-3">
                                            <div className="fw-bold text-dark">{item.guest_name}</div>
                                            <div className="text-muted" style={{ fontSize: '10.5px' }}>{item.invoice_number || '-'} &middot; {item.check_in} &ndash; {item.check_out} &middot; {item.source || '-'}</div>
                                        </td>
                                        <td className="py-2 px-2">
                                            <MissingBadges item={item} />
                                        </td>
                                        <td className="py-2 px-3 text-end" style={{ width: 1 }}>
                                            <Button
                                                size="sm"
                                                style={{ backgroundColor: '#20C997', border: 'none', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}
                                                onClick={() => onFixIt(item.booking_id)}
                                            >
                                                <IconTool size={12} className="me-1" />
                                                Fix it
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </div>

                {meta && meta.last_page > 1 && (
                    <div className="px-3 pb-3">
                        <PaginationComponent
                            totalPages={meta.last_page}
                            currentPage={meta.current_page}
                            onPageChange={setPage}
                        />
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default NeedsAttentionModal;
